import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';

/** Pixels the pointer must move before column resize is committed (avoids width jump on click). */
const RESIZE_DRAG_THRESHOLD_PX = 5;

/**
 * Table column resize: only the explicit handle starts a resize (no "near right edge" on the header).
 * Resize commits only after a small drag threshold, then width follows pointer delta.
 */
@Directive({
  selector: '[appResizableColumn]',
  standalone: true,
})
export class ResizableColumnDirective implements AfterViewInit, OnDestroy {
  @Input() minWidth = 50;
  @Input() maxWidth: number | null = null;
  @Input() resizable = true;
  @Output() resizeStart = new EventEmitter<void>();
  @Output() resizeEnd = new EventEmitter<number>();
  @Output() resizing = new EventEmitter<number>();

  private startX = 0;
  private startWidth = 0;
  private currentWidth = 0;
  private isResizing = false;
  private pendingResize = false;
  private resizeHandle?: HTMLElement;
  private mouseMoveListener?: () => void;
  private mouseUpListener?: () => void;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.createResizeHandle();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private createResizeHandle(): void {
    if (!this.resizable) return;

    const handle = this.renderer.createElement('div');
    this.renderer.addClass(handle, 'resize-handle');
    this.renderer.setStyle(handle, 'position', 'absolute');
    this.renderer.setStyle(handle, 'top', '0');
    this.renderer.setStyle(handle, 'right', '0');
    this.renderer.setStyle(handle, 'width', '6px');
    this.renderer.setStyle(handle, 'height', '100%');
    this.renderer.setStyle(handle, 'cursor', 'col-resize');
    this.renderer.setStyle(handle, 'z-index', '10');
    this.renderer.setStyle(handle, 'background', 'transparent');
    this.renderer.setStyle(handle, 'user-select', 'none');

    this.renderer.listen(handle, 'mouseenter', () => {
      if (!this.isResizing && !this.pendingResize) {
        this.renderer.setStyle(handle, 'background', '#1976d2');
      }
    });

    this.renderer.listen(handle, 'mouseleave', () => {
      if (!this.isResizing && !this.pendingResize) {
        this.renderer.setStyle(handle, 'background', 'transparent');
      }
    });

    this.renderer.listen(handle, 'mousedown', (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.beginResizeIntent(event);
    });

    this.renderer.listen(handle, 'click', (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    });

    const headerCell = this.el.nativeElement;
    if (headerCell && headerCell.tagName === 'TH') {
      this.renderer.setStyle(headerCell, 'position', 'relative');
      this.renderer.appendChild(headerCell, handle);
      this.resizeHandle = handle;
    }
  }

  private beginResizeIntent(event: MouseEvent): void {
    if (!this.resizable) return;

    this.pendingResize = true;
    this.isResizing = false;
    this.startX = event.pageX;
    const actualWidth = this.el.nativeElement.offsetWidth;
    this.startWidth = Math.max(actualWidth, this.minWidth);
    this.currentWidth = this.startWidth;

    this.mouseMoveListener = this.renderer.listen(
      'document',
      'mousemove',
      (moveEvent: MouseEvent) => this.onDocumentMouseMove(moveEvent)
    );
    this.mouseUpListener = this.renderer.listen('document', 'mouseup', () =>
      this.onDocumentMouseUp()
    );
  }

  private onDocumentMouseMove(event: MouseEvent): void {
    if (this.pendingResize && !this.isResizing) {
      if (Math.abs(event.pageX - this.startX) >= RESIZE_DRAG_THRESHOLD_PX) {
        this.commitResize(event);
        this.onMouseMove(event);
      }
      return;
    }
    if (this.isResizing) {
      this.onMouseMove(event);
    }
  }

  /**
   * After parent syncs layout on resizeStart, re-base width and pointer so the first
   * emitted width matches the column edge (no jump on mouseup vs drag).
   */
  private commitResize(event: MouseEvent): void {
    if (this.isResizing) return;

    this.isResizing = true;
    this.pendingResize = false;

    const headerCell = this.el.nativeElement;
    if (headerCell) {
      this.renderer.addClass(headerCell, 'resizing');
      this.renderer.addClass(document.body, 'column-resizing');

      const sortHeader = headerCell.querySelector('.mat-sort-header-container');
      if (sortHeader) {
        this.renderer.setStyle(sortHeader, 'pointer-events', 'none');
      }
    }

    if (this.resizeHandle) {
      this.renderer.setStyle(this.resizeHandle, 'background', '#1976d2');
    }

    this.resizeStart.emit();

    const rect = this.el.nativeElement.getBoundingClientRect();
    let baseline = rect.width > 0 ? Math.round(rect.width) : this.startWidth;
    baseline = Math.max(baseline, this.minWidth);
    if (this.maxWidth !== null) {
      baseline = Math.min(baseline, this.maxWidth);
    }
    this.startWidth = baseline;
    this.startX = event.pageX;
    this.currentWidth = baseline;
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    const diff = event.pageX - this.startX;
    let newWidth = this.startWidth + diff;

    if (newWidth < this.minWidth) {
      newWidth = this.minWidth;
    }
    if (this.maxWidth !== null && newWidth > this.maxWidth) {
      newWidth = this.maxWidth;
    }

    this.currentWidth = newWidth;
    this.resizing.emit(newWidth);
  }

  private onDocumentMouseUp(): void {
    if (this.pendingResize && !this.isResizing) {
      this.pendingResize = false;
      this.cleanup();
      return;
    }
    this.stopResizing();
  }

  private stopResizing(): void {
    if (!this.isResizing) return;

    this.isResizing = false;
    this.pendingResize = false;

    const headerCell = this.el.nativeElement;
    if (headerCell) {
      this.renderer.removeClass(headerCell, 'resizing');
      this.renderer.removeClass(document.body, 'column-resizing');

      const sortHeader = headerCell.querySelector('.mat-sort-header-container');
      if (sortHeader) {
        this.renderer.removeStyle(sortHeader, 'pointer-events');
      }
    }

    if (this.resizeHandle) {
      this.renderer.setStyle(this.resizeHandle, 'background', 'transparent');
    }

    this.resizeEnd.emit(this.currentWidth);
    this.cleanup();
  }

  private cleanup(): void {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
      this.mouseMoveListener = undefined;
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
      this.mouseUpListener = undefined;
    }
  }
}
